'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { Loader2, FileWarning, Download } from 'lucide-react';

import { API_ENDPOINTS } from '@/lib/api/config';
import { useAuthenticatedBlobUrl } from '@/hooks/use-authenticated-blob-url';
import { Button } from '@/components/ui/button';
import { photoScanningQueries } from '../services/photo-scanning.queries';
import type { BatchPageRecord, ScanBatch } from '../types';

interface BatchPdfViewerProps {
  /** Batch id — drives the page-image fallback on browsers without an inline PDF viewer. */
  batchId: number;
  /** Object URL (`blob:`) for the batch PDF, or null while it's still downloading. */
  pdfBlobUrl: string | null;
  /** Batch number — used for the iframe title and the fallback download filename. */
  title: string;
}

/**
 * True when the browser embeds a native PDF viewer for `<iframe src=*.pdf>`.
 *
 * `navigator.pdfViewerEnabled` is the standardized signal: `true` on desktop
 * Chrome/Firefox/Safari, `false` on mobile browsers — which cannot render PDFs
 * inline and instead show a broken-file placeholder. Older engines that don't
 * expose the property report `undefined`, which we treat as unsupported so the
 * always-works page-image fallback kicks in.
 */
function supportsInlinePdf(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  return navigator.pdfViewerEnabled === true;
}

/**
 * Cross-platform batch PDF preview.
 *
 * Desktop browsers embed a PDF viewer, so the native `<iframe>` gives the best
 * experience (text selection, search, zoom, print). Mobile browsers don't —
 * they'd show a broken-file placeholder — so there we render the batch's
 * scanned page images directly through the authenticated image endpoint. The
 * pages are already images on the server, so this is lighter and far more
 * robust than rasterizing the generated PDF in-browser.
 */
export function BatchPdfViewer({
  batchId,
  pdfBlobUrl,
  title
}: BatchPdfViewerProps) {
  const inlineSupported = useMemo(() => supportsInlinePdf(), []);

  if (inlineSupported) {
    if (!pdfBlobUrl) {
      return <ViewerSpinner messageKey='loadingPdf' />;
    }

    return (
      <iframe
        src={pdfBlobUrl}
        className='min-h-0 w-full flex-1'
        title={title}
      />
    );
  }

  return (
    <BatchPageImages batchId={batchId} pdfBlobUrl={pdfBlobUrl} title={title} />
  );
}

function ViewerSpinner({ messageKey }: { messageKey: string }) {
  const t = useTranslations('photoScanning');

  return (
    <div className='text-muted-foreground flex min-h-0 flex-1 flex-col items-center justify-center gap-3'>
      <Loader2 className='h-6 w-6 animate-spin' />
      <span className='text-sm'>{t(messageKey)}</span>
    </div>
  );
}

function BatchPageImages({ batchId, pdfBlobUrl, title }: BatchPdfViewerProps) {
  const t = useTranslations('photoScanning');
  const {
    data: rawBatch,
    isLoading,
    isError
  } = useQuery(photoScanningQueries.detail(batchId));
  const batch = rawBatch?.data as
    | (ScanBatch & { pages: BatchPageRecord[] })
    | undefined;

  // Only pages backed by a real stored image are renderable — `image_url` is
  // null for `scanned://` placeholders and empty slots. Order by sequence so
  // the preview matches the generated PDF's page order.
  const pages = useMemo(
    () =>
      (batch?.pages ?? [])
        .filter((page) => page.image_url !== null)
        .sort((a, b) => a.sequence - b.sequence),
    [batch]
  );

  if (isLoading) {
    return <ViewerSpinner messageKey='renderingPreview' />;
  }

  if (isError || pages.length === 0) {
    return (
      <div className='text-muted-foreground flex min-h-0 flex-1 flex-col items-center justify-center gap-3 p-6 text-center'>
        <FileWarning className='h-8 w-8' />
        <span className='text-sm'>{t('pdfPreviewFailed')}</span>
        {pdfBlobUrl && (
          <Button asChild variant='outline' size='sm'>
            <a href={pdfBlobUrl} download={`${title}.pdf`}>
              <Download className='me-2 h-4 w-4' />
              {t('downloadPdf')}
            </a>
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className='bg-muted/40 min-h-0 flex-1 space-y-3 overflow-y-auto p-3'>
      {pages.map((page, index) => (
        <BatchPageImage
          key={page.id}
          batchId={batchId}
          page={page}
          label={`${title} — ${index + 1}`}
        />
      ))}
    </div>
  );
}

function BatchPageImage({
  batchId,
  page,
  label
}: {
  batchId: number;
  page: BatchPageRecord;
  label: string;
}) {
  // Load through the permissioned API route (Bearer token) so previews work
  // identically on local disk and GCS, then fall back to the direct signed URL.
  const blobUrl = useAuthenticatedBlobUrl(
    API_ENDPOINTS.PHOTO_SCANNING.PAGE_IMAGE(batchId, page.id)
  );
  const src = blobUrl ?? page.image_url;

  if (!src) {
    return (
      <div className='bg-muted/30 mx-auto flex aspect-[1/1.414] w-full max-w-3xl items-center justify-center rounded-sm'>
        <Loader2 className='text-muted-foreground h-5 w-5 animate-spin' />
      </div>
    );
  }

  return (
    /* eslint-disable-next-line @next/next/no-img-element -- authenticated document scan; next/image proxy cannot forward Bearer token */
    <img
      src={src}
      alt={label}
      loading='lazy'
      decoding='async'
      className='mx-auto w-full max-w-3xl rounded-sm shadow-sm'
    />
  );
}
